<template>
  <div
    class="waajo-ticket-dropzone"
    :class="{
      'is-dragging': dragging,
      'is-disabled': !canAccept,
      'is-busy': busy
    }"
    role="button"
    :tabindex="canAccept ? 0 : -1"
    :aria-disabled="canAccept ? 'false' : 'true'"
    @click="openPicker"
    @keydown.enter.prevent="openPicker"
    @keydown.space.prevent="openPicker"
    @dragenter.prevent="onDragEnter"
    @dragover.prevent="onDragOver"
    @dragleave.prevent="onDragLeave"
    @drop.prevent="onDrop"
  >
    <input
      ref="fileInput"
      class="waajo-ticket-dropzone-input"
      type="file"
      multiple
      :accept="accept"
      :disabled="disabled || busy"
      @click.stop
      @change="onFileInput"
    />

    <span class="waajo-ticket-dropzone-icon">
      <i :class="busy ? 'fa fa-spinner fa-spin' : 'fa fa-cloud-upload'"></i>
    </span>

    <template v-if="busy">
      <p class="waajo-ticket-dropzone-title">Mengunggah gambar...</p>
      <div class="waajo-ticket-dropzone-progress">
        <span :style="{ width: normalizedProgress + '%' }"></span>
      </div>
      <p class="waajo-ticket-dropzone-hint">{{ normalizedProgress }}% terkirim</p>
    </template>

    <template v-else-if="remaining <= 0">
      <p class="waajo-ticket-dropzone-title">Kapasitas gambar penuh</p>
      <p class="waajo-ticket-dropzone-hint">
        Maksimal {{ maxFiles }} gambar per tiket. Hapus salah satu gambar untuk
        mengunggah pengganti.
      </p>
    </template>

    <template v-else>
      <p class="waajo-ticket-dropzone-title">Drag &amp; drop gambar di sini</p>
      <p class="waajo-ticket-dropzone-alt">
        atau <span>pilih gambar</span>
      </p>
      <p class="waajo-ticket-dropzone-hint">
        PNG, JPG, JPEG, WebP, GIF · Maks. 2 MB per gambar
      </p>
      <p class="waajo-ticket-dropzone-hint">
        Maksimal {{ maxFiles }} gambar per tiket · Sisa {{ remaining }} slot
      </p>
    </template>
  </div>
</template>

<script>
import {
  TICKET_ATTACHMENT_ACCEPT,
  TICKET_ATTACHMENT_MAX_FILES,
  filterTicketAttachmentFiles,
  ticketAttachmentCapacityReason
} from '@/utils/ticketAttachment'

export default {
  name: 'TicketAttachmentDropzone',
  props: {
    remaining: {
      type: Number,
      default: TICKET_ATTACHMENT_MAX_FILES
    },
    maxFiles: {
      type: Number,
      default: TICKET_ATTACHMENT_MAX_FILES
    },
    busy: {
      type: Boolean,
      default: false
    },
    progress: {
      type: Number,
      default: 0
    },
    disabled: {
      type: Boolean,
      default: false
    },
    accept: {
      type: String,
      default: TICKET_ATTACHMENT_ACCEPT
    }
  },
  data() {
    return {
      dragging: false,
      dragDepth: 0
    }
  },
  computed: {
    canAccept() {
      return !this.disabled && !this.busy && Number(this.remaining) > 0
    },
    normalizedProgress() {
      const value = Number(this.progress) || 0
      return Math.min(100, Math.max(0, Math.round(value)))
    }
  },
  beforeDestroy() {
    this.dragging = false
    this.dragDepth = 0
  },
  methods: {
    openPicker() {
      if (this.disabled || this.busy) return
      if (Number(this.remaining) <= 0) {
        this.$emit('rejected', [
          { name: '', reason: ticketAttachmentCapacityReason({ maxFiles: this.maxFiles }) }
        ])
        return
      }
      if (this.$refs.fileInput) this.$refs.fileInput.click()
    },
    onFileInput(event) {
      const input = event && event.target ? event.target : null
      const files = input && input.files ? input.files : []
      this.processFiles(files)
      if (input) input.value = ''
    },
    onDragEnter() {
      if (!this.canAccept) return
      this.dragDepth += 1
      this.dragging = true
    },
    onDragOver() {
      if (!this.canAccept) return
      this.dragging = true
    },
    onDragLeave() {
      if (!this.canAccept) return
      this.dragDepth = Math.max(0, this.dragDepth - 1)
      if (this.dragDepth === 0) this.dragging = false
    },
    onDrop(event) {
      this.dragDepth = 0
      this.dragging = false
      if (this.disabled || this.busy) return
      const transfer = event && event.dataTransfer ? event.dataTransfer : null
      const files = transfer && transfer.files ? transfer.files : []
      this.processFiles(files)
    },
    processFiles(fileList) {
      if (this.disabled || this.busy) return
      const files = Array.from(fileList || []).filter(Boolean)
      if (!files.length) return

      const remaining = Math.max(0, Number(this.remaining) || 0)
      if (remaining <= 0) {
        this.$emit('rejected', [
          { name: '', reason: ticketAttachmentCapacityReason({ maxFiles: this.maxFiles }) }
        ])
        return
      }

      const result = filterTicketAttachmentFiles(files, remaining, {
        maxFiles: this.maxFiles
      })
      if (result.accepted.length) this.$emit('files', result.accepted)
      if (result.rejected.length) this.$emit('rejected', result.rejected)
    }
  }
}
</script>

<style scoped>
.waajo-ticket-dropzone {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3px;
  width: 100%;
  padding: 18px 16px;
  border: 2px dashed var(--waajo-brand-border);
  border-radius: 12px;
  background: var(--waajo-brand-soft-2);
  text-align: center;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease, box-shadow 0.15s ease;
  outline: none;
}
.waajo-ticket-dropzone:hover,
.waajo-ticket-dropzone:focus {
  border-color: var(--waajo-brand-border-strong);
  background: var(--waajo-brand-soft);
}
.waajo-ticket-dropzone.is-dragging {
  border-color: var(--waajo-brand);
  background: var(--waajo-brand-soft-3);
  box-shadow: 0 0 0 3px var(--waajo-focus-ring);
}
.waajo-ticket-dropzone.is-disabled {
  cursor: not-allowed;
  border-color: #e2e8f0;
  background: #f8fafc;
}
.waajo-ticket-dropzone.is-busy {
  cursor: progress;
  border-color: var(--waajo-brand-border-strong);
  background: var(--waajo-brand-soft);
}
.waajo-ticket-dropzone-input {
  position: absolute;
  width: 1px;
  height: 1px;
  min-height: 0;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  border: 0;
}
.waajo-ticket-dropzone-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  margin-bottom: 3px;
  border-radius: 50%;
  background: #fff;
  border: 1px solid var(--waajo-brand-border);
  color: var(--waajo-brand);
  font-size: 17px;
}
.waajo-ticket-dropzone.is-disabled .waajo-ticket-dropzone-icon {
  color: var(--waajo-text-faint);
  border-color: #e2e8f0;
}
.waajo-ticket-dropzone-title {
  margin: 0;
  font-size: 14px;
  font-weight: 700;
  color: var(--waajo-text);
  line-height: 1.4;
}
.waajo-ticket-dropzone-alt {
  margin: 0;
  font-size: 13px;
  color: var(--waajo-text-muted);
}
.waajo-ticket-dropzone-alt span {
  color: var(--waajo-brand-ink);
  font-weight: 700;
  text-decoration: underline;
}
.waajo-ticket-dropzone-hint {
  margin: 0;
  font-size: 12px;
  line-height: 1.5;
  color: var(--waajo-text-faint);
}
.waajo-ticket-dropzone-progress {
  width: 100%;
  max-width: 320px;
  height: 6px;
  margin: 8px 0 2px;
  overflow: hidden;
  border-radius: 9999px;
  background: #fff;
  border: 1px solid var(--waajo-brand-border);
}
.waajo-ticket-dropzone-progress span {
  display: block;
  height: 100%;
  border-radius: 9999px;
  background: var(--waajo-brand);
  transition: width 0.2s ease;
}
</style>



// WEBPACK FOOTER //
// src/views/ticket/components/TicketAttachmentDropzone.vue